import { type ReactElement } from 'react';
import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Box, CursorTooltip, Text } from '@/ui/primitives';
import {
  tooltipOverlayBodyFontWeight,
  tooltipOverlayCompactLineHeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayMetaFontSize,
  tooltipOverlayMetricFontWeight,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
} from '@/ui/primitives/tooltipOverlayStyles';
import type { ReleaseListItemDto } from '@flowqa/shared';

type FeatureStatusKey = 'draft' | 'planned' | 'ready' | 'released';

type FeatureStatusSegment = {
  key: FeatureStatusKey;
  label: string;
  count: number;
  color: string;
};

type FeatureStatusBreakdown = {
  total: number;
  readyOrReleased: number;
  segments: FeatureStatusSegment[];
};

const FEATURE_STATUS_TOOLTIP_ICON_SIZE = '14px';
const FEATURE_STATUS_TOOLTIP_ROW_PADDING_Y = '2px';

const featureStatusTooltipTextSx = {
  fontSize: tooltipOverlayFontSize,
  fontWeight: tooltipOverlayBodyFontWeight,
  lineHeight: tooltipOverlayCompactLineHeight,
} as const;

const featureStatusTooltipSlotProps = {
  tooltip: {
    sx: {
      px: tooltipOverlayPaddingX,
      py: tooltipOverlayPaddingY,
      maxWidth: '260px',
      width: 'fit-content',
    },
  },
} as const;

const formatStatusPercent = (count: number, total: number): string => {
  if (total <= 0) return '0%';
  const value = Math.round((count / total) * 1000) / 10;
  return Number.isInteger(value) ? `${String(value)}%` : `${value.toFixed(1)}%`;
};

const buildFeatureStatusBreakdown = (row: ReleaseListItemDto): FeatureStatusBreakdown => {
  const counts = (row.features?.items ?? []).reduce<Record<FeatureStatusKey, number>>(
    (accumulator, feature) => {
      accumulator[feature.status] += 1;
      return accumulator;
    },
    { draft: 0, planned: 0, ready: 0, released: 0 },
  );
  const total = counts.draft + counts.planned + counts.ready + counts.released;
  const readyOrReleased = counts.ready + counts.released;

  return {
    total,
    readyOrReleased,
    segments: [
      {
        key: 'draft',
        label: 'Drafted',
        count: counts.draft,
        color: uiTokens.statusColors.neutral,
      },
      {
        key: 'planned',
        label: 'Planned',
        count: counts.planned,
        color: uiTokens.statusColors.blue,
      },
      {
        key: 'ready',
        label: 'Ready',
        count: counts.ready,
        color: uiTokens.statusColors.orange,
      },
      {
        key: 'released',
        label: 'Released',
        count: counts.released,
        color: uiTokens.statusColors.green,
      },
    ],
  };
};

export const ReleaseFeatureStatusCell = ({ row }: { row: ReleaseListItemDto }): ReactElement => {
  const breakdown = buildFeatureStatusBreakdown(row);
  const visibleSegments = breakdown.segments.filter((segment) => segment.count > 0);

  if (breakdown.total <= 0) {
    return (
      <Text role="body" color="textSecondary" component="span">
        -
      </Text>
    );
  }

  return (
    <Box sx={{ minWidth: s('3xl') }}>
      <Box sx={{ alignItems: 'baseline', display: 'flex', gap: s('sm') }}>
        <Text role="body" component="span">
          {formatStatusPercent(breakdown.readyOrReleased, breakdown.total)}
        </Text>
        <Text role="captionSmall" color="textSecondary" component="span">
          ({String(breakdown.readyOrReleased)}/{String(breakdown.total)})
        </Text>
      </Box>
      <CursorTooltip
        title={
          <Box sx={{ display: 'grid', minWidth: '168px', rowGap: s('xxs') }}>
            {visibleSegments.map((segment) => {
              const StatusIcon = resolveGenericStatusCardIcon(segment.key);
              return (
                <Box
                  key={segment.key}
                  sx={{
                    alignItems: 'center',
                    display: 'grid',
                    gridTemplateColumns: `${FEATURE_STATUS_TOOLTIP_ICON_SIZE} ${s('sm')} minmax(0, 1fr) ${s('xs')} max-content max-content`,
                    py: FEATURE_STATUS_TOOLTIP_ROW_PADDING_Y,
                  }}
                >
                  <StatusIcon
                    aria-hidden="true"
                    fontSize="small"
                    sx={{
                      color: segment.color,
                      flex: '0 0 auto',
                      fontSize: FEATURE_STATUS_TOOLTIP_ICON_SIZE,
                      height: FEATURE_STATUS_TOOLTIP_ICON_SIZE,
                      width: FEATURE_STATUS_TOOLTIP_ICON_SIZE,
                    }}
                  />
                  <Box component="span" aria-hidden="true" />
                  <Text
                    role="body"
                    component="span"
                    color="inherit"
                    sx={[
                      featureStatusTooltipTextSx,
                      {
                        fontWeight: tooltipOverlayLabelFontWeight,
                      },
                    ]}
                  >
                    {segment.label}
                  </Text>
                  <Box component="span" aria-hidden="true" />
                  <Text
                    role="body"
                    component="span"
                    color="inherit"
                    sx={[
                      featureStatusTooltipTextSx,
                      {
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: tooltipOverlayMetricFontWeight,
                        minWidth: '3ch',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      },
                    ]}
                  >
                    {segment.count.toLocaleString()}
                  </Text>
                  <Text
                    role="body"
                    component="span"
                    color="inherit"
                    sx={[
                      featureStatusTooltipTextSx,
                      {
                        fontVariantNumeric: 'tabular-nums',
                        color: tooltipOverlayTextColor,
                        fontSize: tooltipOverlayMetaFontSize,
                        ml: s('md'),
                        minWidth: '6ch',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      },
                    ]}
                  >
                    ({formatStatusPercent(segment.count, breakdown.total)})
                  </Text>
                </Box>
              );
            })}
          </Box>
        }
        disableHoverListener={visibleSegments.length === 0}
        arrow
        slotProps={featureStatusTooltipSlotProps}
      >
        <Box
          aria-label="Feature status breakdown"
          role="img"
          sx={{
            backgroundColor: (theme) => theme.app.state.disabledBg,
            borderRadius: r('pill'),
            display: 'flex',
            height: s('sm'),
            mt: s('sm'),
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {visibleSegments.map((segment) => (
            <Box
              key={segment.key}
              sx={{
                backgroundColor: segment.color,
                height: '100%',
                width: `${String((segment.count / breakdown.total) * 100)}%`,
              }}
            />
          ))}
        </Box>
      </CursorTooltip>
    </Box>
  );
};
